import { DocumentsDownloadType, DocumentPrintingPagePlaceholder } from './constants/common';
import { getDocumentData } from '../shared/services/document-api/Documents';

export const ACCEPTED_FILE_TYPES = [
  'png',
  'jpg',
  'jpeg',
  'tiff',
  'tif',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'msg',
  'eml',
];

const MAX_SIMULTANEOUS_UPLOADS = 15;
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface Navigator {
  msSaveOrOpenBlob?: (blob: Blob, fileName: string) => void;
}

export interface PreviewDocumentParams {
  caseId?: string;
  userId?: number;
  beneId?: number;
  documentKeys: string[];
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateFilePropertiesBeforeUpload = (files: File[]): FileValidationResult => {
  const errors: string[] = [];

  const filesWithInvalidExtensions = files.filter(
    (file) => !ACCEPTED_FILE_TYPES.includes((file.name.split('.').pop() || '').toLowerCase())
  );
  const filesThatAreTooLarge = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);

  if (files.length > MAX_SIMULTANEOUS_UPLOADS) {
    errors.push(
      `You tried to upload ${files.length} files but upload is limited to ${MAX_SIMULTANEOUS_UPLOADS} at once.`
    );
  }

  if (filesThatAreTooLarge.length > 0) {
    const largeFileNames = filesThatAreTooLarge.map((file) => file.name).join(', ');
    errors.push(`Some of the files you tried to upload exceed the ${MAX_FILE_SIZE_MB}MB size limit: ${largeFileNames}`);
  }

  if (filesWithInvalidExtensions.length > 0) {
    const invalidFileNames = filesWithInvalidExtensions.map((file) => file.name).join(', ');
    errors.push(
      `Some of the files you tried to upload are unsupported: ${invalidFileNames}. Supported types: ${ACCEPTED_FILE_TYPES.join(
        ', '
      )}. Affected files: ${invalidFileNames}`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const FileUtil = {
  downloadArrayBuffer(arrayBuffer: BlobPart[], fileName: string, type: string) {
    const blob = new window.Blob(arrayBuffer, { type });
    const url = URL.createObjectURL(blob);
    const downloadAnchor = document.getElementById('cobalt-download-anchor') as HTMLAnchorElement | null;
    if (downloadAnchor) {
      downloadAnchor.href = url;
      downloadAnchor.download = fileName;
      downloadAnchor.click();
    } else {
      console.error('Download anchor element not found');
    }
    URL.revokeObjectURL(url);
  },

  async previewArrayBuffer(
    fileDataPromise: Promise<{ arrayBuffer: BlobPart[]; fileName: string; type: string }>,
    placeholder?: string
  ) {
    const hasMsSaveOrOpenBlob = Boolean(
      (window.navigator as Navigator & { msSaveOrOpenBlob?: Function }).msSaveOrOpenBlob
    );
    const previewWindow = hasMsSaveOrOpenBlob ? null : window.open();
    if (previewWindow && placeholder) {
      // CSP-compliant alternative to document.write
      const placeholderElement = previewWindow.document.createElement('div');
      placeholderElement.textContent = placeholder;
      previewWindow.document.body.appendChild(placeholderElement);
    }

    try {
      const { arrayBuffer, fileName, type } = await fileDataPromise;
      const blob = new window.Blob(arrayBuffer, { type });
      if (hasMsSaveOrOpenBlob) {
        (window.navigator as Navigator).msSaveOrOpenBlob!(blob, fileName);
      } else if (previewWindow) {
        const url = URL.createObjectURL(blob);
        const urlLifetime = 180000;
        previewWindow.location.href = url;
        window.setTimeout(() => URL.revokeObjectURL(url), urlLifetime);
      }
    } catch (error) {
      if (previewWindow) {
        previewWindow.close();
      }
      throw error;
    }
  },

  async previewDocument(params: PreviewDocumentParams) {
    const obj = {
      downloadType: DocumentsDownloadType.Print,
      ...params,
    };
    const fileDataPromise = getDocumentData(obj);
    await FileUtil.previewArrayBuffer(fileDataPromise, DocumentPrintingPagePlaceholder);
  },
};
